import { useCallback, useEffect, useMemo, useState } from 'react';
import { ConfigProvider } from 'antd';
import heIL from 'antd/locale/he_IL';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { ProTable, type ProColumns } from '@ant-design/pro-components';
import {
  multiSelectColumn,
  multiSelectColumnFixed,
  numericRangeColumn,
} from '../components/tableHelpers';
import { Money } from '../components/Money';
import { ReceiptAmount } from '../components/ReceiptAmount';
import { EmptyState } from '../components/EmptyState';
import { Loader, loaderIndicator } from '../components/Loader';
import { TabPill } from '../components/TabPill';
import ReportingPeriodFilter from '../components/ReportingPeriodFilter';
import { useRegisterRefresh } from '../components/PageActions';
import { SummaryBar } from '../features/receipts/SummaryBar';
import { SelectionBar } from '../features/receipts/SelectionBar';
import { NewDepositDrawer } from '../features/receipts/NewDepositDrawer';
import { useLocationContext } from '../auth/LocationContext';
import { useProjectContext } from '../auth/ProjectContext';
import { useReceiptsPending } from '../hooks/useReceipts';
import { firstOfMonthIsrael, lastOfMonthIsrael, isoToDisplay } from '../utils/dates';
import type { Receipt, DateField } from '@deposits/shared';

interface Props {
  /** Called once the user successfully creates a deposit; the parent should
      navigate to Tab B and open the PDF drawer for this deposit. */
  onDepositCreated?: (depositId: number) => void;
}

export function ReceiptsPendingTab({ onDepositCreated }: Props = {}) {
  const { selectedLocationId } = useLocationContext();
  const { selectedProjectId } = useProjectContext();

  const [dateField, setDateField] = useState<DateField>('due_date');
  const [period, setPeriod] = useState<{ from: string; to: string }>(() => ({
    from: firstOfMonthIsrael(),
    to: lastOfMonthIsrael(),
  }));
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);


  const params = useMemo(() => {
    if (selectedLocationId == null) return null;
    return {
      dateField,
      from: period.from,
      to: period.to,
      locationId: selectedLocationId,
      projectId: selectedProjectId,
    };
  }, [dateField, period, selectedLocationId, selectedProjectId]);

  // A selection belongs to one project — drop it when the project changes.
  useEffect(() => {
    setSelectedKeys([]);
  }, [selectedProjectId]);

  const { data: receipts = [], isFetching, isError, refetch } = useReceiptsPending(params);

  // Wire the app-shell refresh button to this tab's data.
  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);
  useRegisterRefresh(handleRefresh);

  const selectedReceipts = useMemo(
    () => receipts.filter((r) => selectedKeys.includes(r.external_receipt_id)),
    [receipts, selectedKeys],
  );

  const columns: ProColumns<Receipt>[] = [
    {
      title: 'תאריך הפקה',
      dataIndex: 'issue_date',
      width: 110,
      render: (_, r) => isoToDisplay(r.issue_date),
      sorter: (a, b) => a.issue_date.localeCompare(b.issue_date),
      // When the user toggles the dateField pill to תאריך הפקה, the table
      // remounts (key={dateField} below) and this becomes the active default.
      defaultSortOrder: dateField === 'issue_date' ? 'ascend' : undefined,
    },
    {
      title: 'מספר קבלה',
      dataIndex: 'receipt_number',
      width: 120,
      // The receipt number itself is the link to the Rivhit-hosted PDF.
      // stopPropagation so clicking the link doesn't toggle the row's
      // selection checkbox. Falls back to plain text if no URL is available.
      render: (_, r) =>
        r.receipt_url ? (
          <a
            href={r.receipt_url}
            target="_blank"
            rel="noopener noreferrer"
            dir="ltr"
            className="text-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {r.receipt_number}
          </a>
        ) : (
          <span dir="ltr">{r.receipt_number}</span>
        ),
      // receipt_number is a digit string — numeric compare, not lex.
      sorter: (a, b) => Number(a.receipt_number) - Number(b.receipt_number),
      ...multiSelectColumn<Receipt>(receipts, (r) => r.receipt_number),
    },
    {
      title: 'שם לקוח',
      dataIndex: 'customer_name',
      width: 200,
      render: (_, r) =>
        r.customer_url ? (
          <a href={r.customer_url} target="_blank" rel="noreferrer">
            {r.customer_name}
          </a>
        ) : (
          r.customer_name
        ),
      ellipsis: true,
      sorter: (a, b) => a.customer_name.localeCompare(b.customer_name, 'he'),
      ...multiSelectColumn<Receipt>(receipts, (r) => r.customer_name),
    },
    {
      title: 'מזהה לקוח',
      dataIndex: 'customer_external_id',
      width: 100,
      render: (_, r) => <span dir="ltr">{r.customer_external_id ?? '—'}</span>,
      sorter: (a, b) => {
        // External id is a digit string (Israeli ID-style); numeric compare.
        const av = a.customer_external_id;
        const bv = b.customer_external_id;
        if (!av && !bv) return 0;
        if (!av) return 1;
        if (!bv) return -1;
        return Number(av) - Number(bv);
      },
      ...multiSelectColumn<Receipt>(receipts, (r) => r.customer_external_id),
    },
    {
      title: 'סכום',
      dataIndex: 'amount',
      width: 110,
      render: (_, r) => <ReceiptAmount receipt={r} />,
      sorter: (a, b) => a.amount - b.amount,
      ...numericRangeColumn<Receipt>((r) => r.amount),
    },
    {
      title: 'סוג תקבול',
      dataIndex: 'payment_type',
      width: 100,
      render: (_, r) => (r.payment_type === 'cash' ? 'מזומן' : "צ'ק"),
      // Sort by displayed label so מזומן < צ'ק groups by type.
      sorter: (a, b) =>
        (a.payment_type === 'cash' ? 'מזומן' : "צ'ק").localeCompare(
          b.payment_type === 'cash' ? 'מזומן' : "צ'ק",
          'he',
        ),
      ...multiSelectColumnFixed<Receipt>(
        [
          { value: 'cash', label: 'מזומן' },
          { value: 'check', label: "צ'ק" },
        ],
        (record, value) => record.payment_type === value,
      ),
    },
    {
      title: 'תאריך פירעון',
      dataIndex: 'due_date',
      width: 110,
      render: (_, r) => isoToDisplay(r.due_date),
      // Cash receipts have due_date=null and are filtered out by the server
      // when dateField=due_date, but defensively sort nulls to the end.
      sorter: (a, b) => {
        if (a.due_date === b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return a.due_date.localeCompare(b.due_date);
      },
      // Default view loads with dateField='due_date', so the table sorts
      // oldest-first by due date. Conditional so that toggling the pill to
      // תאריך הפקה (which remounts the table via key={dateField}) moves the
      // default sort to that column instead.
      defaultSortOrder: dateField === 'due_date' ? 'ascend' : undefined,
    },
    {
      title: "מספר צ'ק",
      dataIndex: 'check_number',
      width: 110,
      render: (_, r) =>
        r.check_number ? <span dir="ltr">{r.check_number}</span> : '—',
      sorter: (a, b) => {
        // check_number is a digit string; numeric compare, nulls last.
        if (!a.check_number && !b.check_number) return 0;
        if (!a.check_number) return 1;
        if (!b.check_number) return -1;
        return Number(a.check_number) - Number(b.check_number);
      },
      ...multiSelectColumn<Receipt>(receipts, (r) => r.check_number),
    },
    {
      title: 'בנק',
      dataIndex: 'bank_code',
      width: 130,
      ellipsis: true,
      render: (_, r) => r.bank_code ?? '—',
      sorter: (a, b) => (a.bank_code || '').localeCompare(b.bank_code || '', 'he'),
      ...multiSelectColumn<Receipt>(receipts, (r) => r.bank_code),
    },
    {
      title: "מס' סניף",
      dataIndex: 'branch_number',
      width: 90,
      render: (_, r) =>
        r.branch_number ? <span dir="ltr">{r.branch_number}</span> : '—',
      sorter: (a, b) => {
        if (!a.branch_number && !b.branch_number) return 0;
        if (!a.branch_number) return 1;
        if (!b.branch_number) return -1;
        return Number(a.branch_number) - Number(b.branch_number);
      },
      ...multiSelectColumn<Receipt>(receipts, (r) => r.branch_number),
    },
    {
      title: "מס' חשבון",
      dataIndex: 'account_number',
      width: 130,
      render: (_, r) =>
        r.account_number ? <span dir="ltr">{r.account_number}</span> : '—',
      sorter: (a, b) =>
        (a.account_number || '').localeCompare(b.account_number || '', 'he'),
      ...multiSelectColumn<Receipt>(receipts, (r) => r.account_number),
    },
  ];

  if (selectedLocationId == null) {
    return <Loader fullScreen />;
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Filter bar */}
      <div className="flex items-center gap-2 sm:gap-3 flex-wrap pb-3">
        <ReportingPeriodFilter
          value={period}
          onChange={(from, to) => setPeriod({ from, to })}
        />
        <TabPill<DateField>
          value={dateField}
          onChange={setDateField}
          options={[
            { value: 'due_date', label: 'תאריך פירעון' },
            { value: 'issue_date', label: 'תאריך הפקה' },
          ]}
        />
      </div>

      {/* Table area — flex-1 takes remaining height. The nested ConfigProvider
          repeats locale + getPopupContainer because antd 5's ProTable doesn't
          reliably inherit context for its internal Dropdown/Pagination. */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <ConfigProvider locale={heIL} getPopupContainer={() => document.body}>
          <ProTable<Receipt>
            // Remount the table on dateField change so the new column's
            // defaultSortOrder='ascend' takes effect (defaultSortOrder is
            // mount-only; without a remount, antd's internal sort state
            // sticks to whatever was set on first render).
            key={dateField}
            rowKey="external_receipt_id"
            columns={columns}
            dataSource={receipts}
            loading={
              isFetching ? { spinning: true, indicator: loaderIndicator('md') } : false
            }
            search={false}
            options={false}
            showSorterTooltip={false}
            defaultSize="small"
            rowSelection={{
              selectedRowKeys: selectedKeys,
              onChange: (keys) => setSelectedKeys(keys.map(String)),
            }}
            pagination={{
              // Uncontrolled — antd manages currentPage + pageSize internally.
              // Passing `pageSize` (controlled) without an onChange handler
              // makes the size-changer dropdown a no-op.
              defaultPageSize: 50,
              showSizeChanger: true,
              pageSizeOptions: ['25', '50', '100', '200'],
              size: 'small',
              position: ['bottomLeft'],
              className: 'he-pagination',
              itemRender: (_page, type, original) => {
                if (type === 'prev')
                  return (
                    <button type="button" className="ant-pagination-item-link">
                      <RightOutlined />
                    </button>
                  );
                if (type === 'next')
                  return (
                    <button type="button" className="ant-pagination-item-link">
                      <LeftOutlined />
                    </button>
                  );
                return original;
              },
              locale: heIL.Pagination,
            }}
            locale={{
              emptyText: isError ? (
                <EmptyState
                  title="שגיאה בטעינת תקבולים"
                  description="נסה שוב או רענן את העמוד"
                />
              ) : (
                <EmptyState
                  title="אין תקבולים להפקדה"
                  description="כל התקבולים בטווח שנבחר כבר הופקדו או שאין כאלה"
                />
              ),
            }}
            scroll={{ x: 'max-content', y: 'calc(100vh - 360px)' }}
            tableStyle={{ background: 'white' }}
            {...({ getPopupContainer: () => document.body } as any)}
          />
        </ConfigProvider>
      </div>

      {/* Always-on summary bar (filtered totals, blue-bg) */}
      <SummaryBar receipts={receipts} />

      {/* Floating selection bar — portaled, only visible when rows selected */}
      <SelectionBar
        selectedReceipts={selectedReceipts}
        onCreateDeposit={() => setDrawerOpen(true)}
        onClear={() => setSelectedKeys([])}
      />

      <NewDepositDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCreated={(depositId) => {
          setDrawerOpen(false);
          setSelectedKeys([]);
          onDepositCreated?.(depositId);
        }}
        selected={selectedReceipts}
      />
    </div>
  );
}
